import axios from 'axios';

// Create base API instance
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Create axios instance with default config
export const authAPI = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Create axios instance for authenticated requests
export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ── Workspace context (for company users switching into a super-admin workspace) ──
export const getWorkspaceId   = () => localStorage.getItem('workspaceId') || '';
export const getWorkspaceName = () => localStorage.getItem('workspaceName') || '';
export const setWorkspace     = (id, name) => {
  localStorage.setItem('workspaceId', id);
  if (name !== undefined) localStorage.setItem('workspaceName', name || '');
};
export const clearWorkspace   = () => {
  localStorage.removeItem('workspaceId');
  localStorage.removeItem('workspaceName');
};

// Request interceptor to add auth token + active workspace
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const workspaceId = localStorage.getItem('workspaceId');
    if (workspaceId) {
      config.headers['X-Workspace-Id'] = workspaceId;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle common errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 403 Forbidden - insufficient permissions
    if (error.response?.status === 403) {
      console.error('Access denied:', error.response?.data?.message);
    }

    return Promise.reject(error);
  }
);

// API Service functions
export const authService = {
  login: (credentials) => authAPI.post('/auth/login', credentials),
  register: (userData) => authAPI.post('/auth/register', userData),
  getProfile: () => authAPI.get('/auth/me'),
  updateProfile: (profileData) => authAPI.put('/auth/profile', profileData),
  changePassword: (passwordData) => authAPI.post('/auth/change-password', passwordData),
  logout: () => authAPI.post('/auth/logout'),
};

export const userService = {
  getUsers: (params) => api.get('/users', { params }),
  getAssignableUsers: (params) => api.get('/users/assignable', { params }),
  getUser: (id) => api.get(`/users/${id}`),
  createUser: (userData) => api.post('/users', userData),
  updateUser: (id, userData) => api.put(`/users/${id}`, userData),
  deleteUser: (id) => api.delete(`/users/${id}`),
  resetPassword: (id, data) => api.post(`/users/${id}/reset-password`, data),
  getCredentials: (id) => api.get(`/users/${id}/credentials`),
  updatePermissions: (id, permissions) => api.put(`/users/${id}/permissions`, { permissions }),
  getUserStats: () => api.get('/users/stats/overview'),
  getHierarchy: () => api.get('/users/hierarchy'),
  // Everyone the list filters match, with login details (Download)
  exportUsers: (params) => api.get('/users/export', { params, timeout: 120000 }),
  // dryRun: only validate the sheet and report what would be created
  bulkUpload: (file, { dryRun = false } = {}) => {
    const form = new FormData();
    form.append('file', file);
    return api.post('/users/bulk-upload', form, {
      params: dryRun ? { dryRun: 'true' } : undefined,
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
  },
};

export const backupService = {
  list:    ()    => api.get('/backups'),
  runNow:  ()    => api.post('/backups/run', {}, { timeout: 120000 }),
  excel:   (key) => api.get('/backups/download', { params: { key, format: 'excel' }, responseType: 'blob', timeout: 120000 }),
  rawLink: (key) => api.get('/backups/download', { params: { key, format: 'raw' } }),
};

// Recycle Bin — deleted leads, restorable for a week
export const leadTrashService = {
  list:    (params) => api.get('/lead-trash', { params }),
  restore: (ids)    => api.post('/lead-trash/restore', { ids }),
};

export const leadService = {
  getLeads: (params) => {
    // Strip empty/null/undefined values so they don't override backend logic
    const clean = Object.fromEntries(Object.entries(params || {}).filter(([, v]) => v != null && v !== ''));
    return api.get('/leads', { params: clean });
  },
  getLead: (id) => api.get(`/leads/${id}`),
  createLead: (leadData) => api.post('/leads', leadData),
  updateLead: (id, leadData) => api.put(`/leads/${id}`, leadData),
  deleteLead: (id) => api.delete(`/leads/${id}`),
  assignLead: (id, assignData) => api.post(`/leads/${id}/assign`, assignData),
  getLeadStats: (params) => {
    const clean = Object.fromEntries(Object.entries(params || {}).filter(([, v]) => v != null && v !== ''));
    return api.get('/leads/stats/overview', { params: clean });
  },
  exportLeads: (params) => {
    const clean = Object.fromEntries(Object.entries(params || {}).filter(([, v]) => v != null && v !== ''));
    return api.get('/leads/export', { params: clean, timeout: 60000 });
  },
  getFunnelReport: (params) => api.get('/leads/funnel-report', { params }),
  getLostReport:   (params) => api.get('/leads/lost-report', { params }),
  getTATReport:    (params) => api.get('/leads/tat-report', { params }),
  getGroupReport:  (params) => api.get('/leads/group-report', { params }),
  getLeadActivity: (id) => api.get(`/leads/${id}/activity`),
  markViewed:      (id) => api.post(`/leads/${id}/view`),
  forwardLead:     (id, data) => api.post(`/leads/${id}/forward`, data),
  reopenLead:      (id, data) => api.post(`/leads/${id}/reopen`, data),
  bulkAssign:      (data) => api.post('/leads/bulk-assign', data, { timeout: 60000 }),
  bulkDelete:      (data) => api.post('/leads/bulk-delete', data, { timeout: 60000 }),
  bulkForward:     (data) => api.post('/leads/bulk-forward', data, { timeout: 60000 }),
  bulkUpload:      (file, assignedTo) => {
    const form = new FormData();
    form.append('file', file);
    if (assignedTo) form.append('assignedTo', assignedTo);
    return api.post('/leads/bulk-upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

export const followupService = {
  getFollowups: (params) => api.get('/followups', { params }),
  getFollowup: (id) => api.get(`/followups/${id}`),
  createFollowup: (followupData) => api.post('/followups', followupData),
  updateFollowup: (id, followupData) => api.put(`/followups/${id}`, followupData),
  deleteFollowup: (id) => api.delete(`/followups/${id}`),
  completeFollowup: (id, completionData) => api.post(`/followups/${id}/complete`, completionData),
  getTodayFollowups: () => api.get('/followups/today/list'),
  getOverdueFollowups: () => api.get('/followups/overdue/list'),
  getFollowupStats: () => api.get('/followups/stats/overview'),
};

export const brandService = {
  getBrands:   ()                       => api.get('/brands'),
  createBrand: (data)                   => api.post('/brands', data),
  updateBrand: (id, data)               => api.put(`/brands/${id}`, data),
  deleteBrand: (id)                     => api.delete(`/brands/${id}`),
  addModel:    (brandId, data)          => api.post(`/brands/${brandId}/models`, data),
  deleteModel: (brandId, modelId)       => api.delete(`/brands/${brandId}/models/${modelId}`),
};


export const fbIntegrationService = {
  getSettings:       ()                          => api.get('/fb-integration/settings'),
  saveSettings:      (data)                      => api.post('/fb-integration/settings', data),
  regenerateToken:   ()                          => api.post('/fb-integration/settings/regenerate-token'),
  getAuthUrl:        ()                          => api.get('/fb-integration/auth-url'),
  getMyAccount:      ()                          => api.get('/fb-integration/my-account'),
  disconnect:        ()                          => api.delete('/fb-integration/disconnect'),
  // Walks every business portfolio on the FB account — can take a while.
  refreshPages:      ()                          => api.post('/fb-integration/refresh-pages', {}, { timeout: 120000 }),
  togglePage:        (pageId)                    => api.put(`/fb-integration/pages/${pageId}/toggle`),
  routePage:         (pageId, routeToUserId)     => api.put(`/fb-integration/pages/${pageId}/route`, { routeToUserId }),
  getAllAccounts:     ()                          => api.get('/fb-integration/all-accounts'),
  disconnectAccount: (accountId)                 => api.delete(`/fb-integration/accounts/${accountId}`),
  syncLeads:         (hours = 48)                => api.post('/fb-integration/sync-leads', { hours }),
  getFormHealth:     (days = 30)                 => api.get('/fb-integration/form-health', { params: { days } }),
};

export const leadDistributionService = {
  getDealers:            ()             => api.get('/lead-distribution/dealers'),
  updateDealerTerritory: (id, data)     => api.put(`/lead-distribution/dealers/${id}/territory`, data),
  getMyTerritory:        ()             => api.get('/lead-distribution/my-territory'),
  getReviewQueue:        (params)       => api.get('/lead-distribution/review-queue', { params }),
  resolveReviewItem:     (leadId, data) => api.post(`/lead-distribution/review-queue/${leadId}/resolve`, data),
  resolveReviewItems:    (data)         => api.post('/lead-distribution/review-queue/resolve', data, { timeout: 60000 }),
  getStats:              ()             => api.get('/lead-distribution/stats'),
  getLogs:               (params)       => api.get('/lead-distribution/logs', { params }),
  getQuotas:             ()             => api.get('/lead-distribution/quotas'),
  updateQuota:           (id, data)     => api.put(`/lead-distribution/quotas/${id}`, data),
};

export const emailSettingsService = {
  getSettings:  ()     => api.get('/email-settings'),
  saveSettings: (data) => api.post('/email-settings', data),
  sendTest:     ()     => api.post('/email-settings/test'),
};

export const whatsappService = {
  getSettings:      ()                    => api.get('/whatsapp/settings'),
  regenerateToken:  ()                    => api.post('/whatsapp/settings/regenerate-token'),
  getNumbers:       ()                    => api.get('/whatsapp/numbers'),
  addNumber:        (data)                => api.post('/whatsapp/numbers', data),
  updateNumber:     (id, data)            => api.patch(`/whatsapp/numbers/${id}`, data),
  removeNumber:     (id)                  => api.delete(`/whatsapp/numbers/${id}`),
  getNumberTeam:    (id)                  => api.get(`/whatsapp/numbers/${id}/team`),
  setDealerLimit:   (dealerId, limit)     => api.put(`/whatsapp/dealers/${dealerId}/limit`, { limit }),
  getTemplates:     (leadId)              => api.get('/whatsapp/templates', { params: { leadId } }),
  listMessageTemplates:  (numberId)       => api.get(`/whatsapp/numbers/${numberId}/message-templates`),
  createMessageTemplate: (numberId, data) => api.post(`/whatsapp/numbers/${numberId}/message-templates`, data),
  deleteMessageTemplate: (numberId, name) => api.delete(`/whatsapp/numbers/${numberId}/message-templates/${encodeURIComponent(name)}`),
  getMessages:      (leadId)              => api.get(`/whatsapp/messages/${leadId}`),
  sendMessage:      (data)                => api.post('/whatsapp/send', data),
};

export const leadActivityService = {
  getLeadActivities: (leadId) => api.get(`/lead-activities/${leadId}`),
  createLeadActivity: (data) => api.post('/lead-activities', data),
  updateLeadActivity: (id, data) => api.put(`/lead-activities/${id}`, data),
  deleteLeadActivity: (id) => api.delete(`/lead-activities/${id}`),
};

export const rolePermissionService = {
  getRolePermissions: () => api.get('/role-permissions'),
  updateRolePermissions: (role, permissions) => api.put(`/role-permissions/${role}`, { permissions }),
};

export const websiteSourceService = {
  list:          () => api.get('/website-sources'),
  create:        (data) => api.post('/website-sources', data),
  update:        (id, data) => api.put(`/website-sources/${id}`, data),
  regenerateKey: (id) => api.post(`/website-sources/${id}/regenerate-key`),
  remove:        (id) => api.delete(`/website-sources/${id}`),
};

export const googleIntegrationService = {
  getSettings:   () => api.get('/google-integration/settings'),
  saveSettings:  (data) => api.put('/google-integration/settings', data),
  regenerateKey: () => api.post('/google-integration/regenerate-key'),
};

// Dealer CRM integrations. Everything but leadStatus is super admin only.
export const crmIntegrationService = {
  meta:        () => api.get('/crm-integrations/meta'),
  list:        () => api.get('/crm-integrations'),
  create:      (data) => api.post('/crm-integrations', data),
  update:      (id, data) => api.put(`/crm-integrations/${id}`, data),
  remove:      (id) => api.delete(`/crm-integrations/${id}`),
  // A real call can take the integration's full timeout (up to 60 s).
  test:        (id, data) => api.post(`/crm-integrations/${id}/test`, data, { timeout: 70000 }),
  push:        (id, leadIds) => api.post(`/crm-integrations/${id}/push`, { leadIds }),
  deliveries:  (params) => api.get('/crm-integrations/deliveries', { params }),
  logs:        (params) => api.get('/crm-integrations/logs', { params }),
  resend:      (deliveryId) => api.post(`/crm-integrations/deliveries/${deliveryId}/resend`),
  leadStatus:  (ids) => api.get('/crm-integrations/lead-status', { params: { ids: ids.join(',') } }),
};

export const dashboardService = {
  getDashboardData: () => api.get('/dashboard/overview'),
  // A long range ("All") over a big workspace can take more than the default 10 s
  getAnalytics:     (params) => api.get('/dashboard/analytics', { params, timeout: 30000 }),
};

export const companyService = {
  getMe:           () => api.get('/company/me'),
  getWorkspaces:   () => api.get('/company/workspaces'),
  createWorkspace: (data) => api.post('/company/workspaces', data),
  updateWorkspace: (tenantId, data) => api.patch(`/company/workspaces/${tenantId}`, data),
  getMetaApp:      () => api.get('/company/meta-app'),
  saveMetaApp:     (data) => api.put('/company/meta-app', data),
  transferLeads:   (data) => api.post('/company/leads/transfer', data),
  getGroupReport:  (params) => api.get('/company/group-report', { params }),
  getWorkspaceLeadsPreview: (tenantId, params) => api.get(`/company/workspaces/${tenantId}/leads`, { params }),
};

// Utility function to handle API errors
export const handleApiError = (error) => {
  if (error.response) {
    // The request was made and the server responded with a status code
    // that falls out of the range of 2xx
    return {
      message: error.response.data.message || 'Server error',
      status: error.response.status,
      data: error.response.data,
    };
  } else if (error.request) {
    // The request was made but no response was received
    return {
      message: 'Network error. Please check your connection.',
      status: null,
      data: null,
    };
  } else {
    // Something happened in setting up the request that triggered an Error
    return {
      message: error.message || 'An unexpected error occurred',
      status: null,
      data: null,
    };
  }
};

// Utility function to build query parameters
export const buildQueryParams = (params) => {
  const searchParams = new URLSearchParams();
  
  Object.keys(params).forEach(key => {
    const value = params[key];
    if (value !== undefined && value !== null && value !== '') {
      if (Array.isArray(value)) {
        value.forEach(item => searchParams.append(key, item));
      } else {
        searchParams.append(key, value);
      }
    }
  });
  
  return searchParams.toString();
};

// Export default API instance
export default api;
